import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Clock, Shirt, CheckCircle2, Check, PlayCircle } from "lucide-react";

const Landing = () => {

  useEffect(() => {
    document.title = `BackOffice - Automated Client Intake & Job Tracking`;
  }, []);

  const navigate = useNavigate();
  const logoUrl = "https://yourpersonalshopper-storage.sfo3.digitaloceanspaces.com/assets/bo_logo.webp";

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1a1f] via-[#005f6b] to-[#0a1a1f] pb-8">
      {/* Navigation */}
      <nav className="px-6 py-4 flex justify-between items-center max-w-7xl mx-auto border-b border-[#005f6b]/20 backdrop-blur-sm">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
          <img src={logoUrl} alt="Logo" className="w-12 h-12 rounded-full" />
          <span className="text-xl font-semibold text-[#d4af37]">BackOffice</span>
        </div>
        <div className="flex gap-3">
          <Button
            data-testid="login-btn"
            variant="ghost"
            onClick={() => navigate("/login")}
            className="text-[#f8f8f8] hover:text-[#00ced1] hover:bg-[#005f6b]/20"
          >
            Login
          </Button>
          <Button
            data-testid="signup-btn"
            onClick={() => navigate("/signup")}
            className="bg-[#00ced1] hover:bg-[#00ced1]/90 text-[#0a1a1f] font-semibold hidden md:block"
          >
            Start Free Trial
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center space-y-6">
          <div className="inline-block">
            <span className="text-sm font-medium text-[#d4af37] bg-[#d4af37]/10 px-4 py-2 rounded-full border border-[#d4af37]/30">
              7-Day Free Trial • No Credit Card Required
            </span>
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-[#f8f8f8] leading-tight">
            Why professionals love <br />
            <span className="text-[#d4af37]">BackOffice</span>
          </h1>

          <p className="text-lg text-[#f8f8f8]/80 max-w-2xl mx-auto">
            BackOffice is your AI-powered client intake and back office assistant. Generate service sheets, organize jobs, centralize appointments and run your business with zero admin chaos.
          </p>

          <p className="text-lg text-[#f8f8f8]/80 max-w-2xl mx-auto">
            BackOffice turns unpaid admin time into billable hours—without sacrificing personalization.
          </p>

          <div className="flex flex-col md:flex-row gap-4 justify-center pt-4">
            <Button
              data-testid="get-started-hero-btn"
              onClick={() => navigate("/signup")}
              size="lg"
              className="bg-[#00ced1] hover:bg-[#00ced1]/90 text-[#0a1a1f] font-semibold text-lg px-8 py-6 rounded-xl"
            >
              Start Your Free Trial
            </Button>
            <Button
              data-testid="view-pricing-btn"
              onClick={() => navigate("/pricing")}
              size="lg"
              variant="outline"
              className="border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37]/10 text-lg px-8 py-6 rounded-xl"
            >
              View Pricing
            </Button>
          </div>
        </div>

        {/* Video Section */}
        <div className="mt-16 max-w-4xl mx-auto">
          <div className="bg-[#0a1a1f]/60 backdrop-blur-sm rounded-2xl border border-[#d4af37]/30 overflow-hidden">
            <div className="aspect-video">
              <iframe
                data-testid="explainer-video"
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/xrApWpbte7I"
                title="BackOffice Explainer Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              ></iframe>
            </div>
          </div>
          <p className="text-center text-[#f8f8f8]/70 mt-4">
            <PlayCircle className="w-4 h-4 inline mr-2" />
            See how BackOffice transforms your intake experience
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-8 mt-24">
          <div className="bg-[#0a1a1f]/60 backdrop-blur-sm p-8 rounded-2xl border border-[#d4af37]/20 hover:border-[#d4af37]/40 transition-all">
            <div className="w-12 h-12 bg-[#00ced1]/20 rounded-xl flex items-center justify-center mb-4 border border-[#00ced1]/30">
              <Clock className="w-6 h-6 text-[#00ced1]" />
            </div>
            <h3 className="text-xl font-semibold text-[#f8f8f8] mb-2">One Link. Smart AI Intake</h3>
            <p className="text-[#f8f8f8]/70">
              Send one link and let BackOffice handle client intake for you. Our AI-powered system gathers client details and automatically creates a clean, professional service sheet—before the appointment begins.
            </p>
          </div>

          <div className="bg-[#0a1a1f]/60 backdrop-blur-sm p-8 rounded-2xl border border-[#d4af37]/20 hover:border-[#d4af37]/40 transition-all">
            <div className="w-12 h-12 bg-[#00ced1]/20 rounded-xl flex items-center justify-center mb-4 border border-[#00ced1]/30">
              <Shirt className="w-6 h-6 text-[#00ced1]" />
            </div>
            <h3 className="text-xl font-semibold text-[#f8f8f8] mb-2">Stay Organized. Stay Present.</h3>
            <p className="text-[#f8f8f8]/70">
              BackOffice syncs with your Google Calendar and keeps past, current, and upcoming appointments organized in one place, so you can show up calm, prepared, and focused on your work.
            </p>
          </div>

          <div className="bg-[#0a1a1f]/60 backdrop-blur-sm p-8 rounded-2xl border border-[#d4af37]/20 hover:border-[#d4af37]/40 transition-all">
            <div className="w-12 h-12 bg-[#00ced1]/20 rounded-xl flex items-center justify-center mb-4 border border-[#00ced1]/30">
              <CheckCircle2 className="w-6 h-6 text-[#00ced1]" />
            </div>
            <h3 className="text-xl font-semibold text-[#f8f8f8] mb-2">Look Professional Without a Team</h3>
            <p className="text-[#f8f8f8]/70">
              BackOffice gives solo entrepreneurs the polish of a full operation—streamlining your backend while creating a professional, seamless experience for your clients.
            </p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-24 bg-gradient-to-r from-[#005f6b] to-[#00ced1] rounded-3xl p-12 text-center border border-[#d4af37]/20">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-[#f8f8f8]">
            Ready to Transform Your Business?
          </h2>
          <p className="text-lg text-[#f8f8f8]/90 mb-8 max-w-2xl mx-auto">
            Start your 7-day free trial today. No credit card required.
          </p>
          <Button
            data-testid="cta-signup-btn"
            onClick={() => navigate("/signup")}
            size="lg"
            className="bg-[#d4af37] text-[#0a1a1f] hover:bg-[#d4af37]/90 font-semibold text-lg px-8 py-6 rounded-xl"
          >
            Start Free Trial
          </Button>
        </div>
      </div>

      <footer className="text-center text-[#f8f8f8]/70 mt-12 text-sm space-x-4">
        <span>© 2025 BackOffice. All rights reserved.</span>
        <a className="cursor-pointer text-[#d4af37] hover:text-[#00ced1]" href="/privacy-policy">Privacy Policy</a>
        <a className="cursor-pointer text-[#d4af37] hover:text-[#00ced1]" href="/help-guide">Help Guide</a>
      </footer>
    </div>
  );
};

export default Landing;
